export const DATE_RANGE_ATTRIBUTES = new Set([
  'created_at',
  'last_activity_at',
]);

export const DATE_RANGE_OPERATOR = 'is_between';

const normalizeDateValue = value => {
  if (Array.isArray(value)) return value[0] || '';
  if (value && typeof value === 'object') return value.id || value.name || '';
  return value || '';
};

const pad = n => String(n).padStart(2, '0');

// Filter values are sent as ISO-8601 UTC so the API (Time.zone.parse, UTC) and
// the browser-side matcher (new Date) agree. Naive "YYYY-MM-DDTHH:mm" strings
// are read as UTC by the server but as local time by the browser.
export const toIsoDateTime = (date, time, fallbackTime = '00:00') => {
  if (!date) return '';
  const parsed = new Date(`${date}T${time || fallbackTime}`);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

// Local date/time parts for the inputs; accepts ISO or legacy naive strings.
export const splitLocalDateTime = value => {
  if (!value) return { date: '', time: '' };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };
  return {
    date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
  };
};

export const formatLocalDateTime = value => {
  const { date, time } = splitLocalDateTime(value);
  return date ? `${date} ${time}` : '';
};

/**
 * Collapse adjacent is_from + is_to pairs for the same date attribute into a single is_between row.
 * Keeps API/backend contract (is_from/is_to) while editing as one range in the UI.
 */
export const collapseDateRangeFilters = filters => {
  if (!Array.isArray(filters) || filters.length === 0) return filters;

  const collapsed = [];
  let index = 0;

  while (index < filters.length) {
    const current = filters[index];
    const next = filters[index + 1];
    const attributeKey = current.attributeKey || current.attribute_key;
    const operator = current.filterOperator || current.filter_operator;
    const nextAttributeKey = next?.attributeKey || next?.attribute_key;
    const nextOperator = next?.filterOperator || next?.filter_operator;

    const isDateRangePair =
      DATE_RANGE_ATTRIBUTES.has(attributeKey) &&
      next &&
      nextAttributeKey === attributeKey &&
      ((operator === 'is_from' && nextOperator === 'is_to') ||
        (operator === 'is_to' && nextOperator === 'is_from'));

    if (isDateRangePair) {
      const fromFilter = operator === 'is_from' ? current : next;
      const toFilter = operator === 'is_to' ? current : next;
      collapsed.push({
        ...current,
        attributeKey,
        filterOperator: DATE_RANGE_OPERATOR,
        values: {
          from: normalizeDateValue(fromFilter.values),
          to: normalizeDateValue(toFilter.values),
        },
        queryOperator:
          next.queryOperator ||
          next.query_operator ||
          current.queryOperator ||
          'and',
      });
      index += 2;
    } else if (
      DATE_RANGE_ATTRIBUTES.has(attributeKey) &&
      (operator === 'is_from' || operator === 'is_to')
    ) {
      collapsed.push({
        ...current,
        attributeKey,
        filterOperator: DATE_RANGE_OPERATOR,
        values: {
          from:
            operator === 'is_from' ? normalizeDateValue(current.values) : '',
          to: operator === 'is_to' ? normalizeDateValue(current.values) : '',
        },
      });
      index += 1;
    } else {
      collapsed.push(current);
      index += 1;
    }
  }

  return collapsed;
};

/**
 * Expand UI is_between date range rows back into is_from / is_to conditions for the API.
 * - Only From → From + To=now (up to current datetime)
 * - Only To → To only (from the beginning, no lower bound)
 * - Both → From + To as entered
 */
export const expandDateRangeFilters = filters => {
  if (!Array.isArray(filters) || filters.length === 0) return filters;

  return filters.flatMap(filter => {
    const attributeKey = filter.attributeKey || filter.attribute_key;
    const operator = filter.filterOperator || filter.filter_operator;

    if (
      !DATE_RANGE_ATTRIBUTES.has(attributeKey) ||
      operator !== DATE_RANGE_OPERATOR
    ) {
      return [filter];
    }

    const range =
      filter.values &&
      typeof filter.values === 'object' &&
      !Array.isArray(filter.values)
        ? filter.values
        : { from: '', to: '' };
    const from = range.from || '';
    let to = range.to || '';
    const queryOperator =
      filter.queryOperator || filter.query_operator || 'and';

    // Only "From" filled → bound the range to now
    if (from && !to) {
      to = new Date().toISOString();
    }

    const expanded = [];

    if (from) {
      expanded.push({
        ...filter,
        attributeKey,
        filterOperator: 'is_from',
        values: from,
        queryOperator: to ? 'and' : queryOperator,
      });
    }

    if (to) {
      expanded.push({
        ...filter,
        attributeKey,
        filterOperator: 'is_to',
        values: to,
        queryOperator,
      });
    }

    return expanded.length ? expanded : [filter];
  });
};
